<template>
    <pop-in
        container-style="task-dialog"
        :modal="true"
        @close="onPopinClose">
        <template #popin-body>
            <!-- Message (+icon) -->
            <div class="task-message-container">
                <template v-if="iconName.length > 0">
                    <stop-icon v-if="iconName === 'stop'" class="medium-icon task-message-icon"></stop-icon>
                </template>
                <div v-if="message != ''" class="task-message">{{ messageText }}</div>
            </div>
            <!-- buttons -->
            <div class="task-buttons">
                <button
                    v-if="button1Label != ''"
                    tabindex="0"
                    class="theme-button"
                    @click="onButton1Click"
                    @keypress.enter="onButton1Click">
                    <span class="theme-button-text">{{ button1Label }}</span>
                </button>
                <button
                    v-if="button2Label != ''"
                    tabindex="0"
                    class="theme-button"
                    @click="onButton2Click"
                    @keypress.enter="onButton2Click">
                    <span class="theme-button-text">{{ button2Label }}</span>
                </button>
                <button
                    v-if="button3Label != ''"
                    tabindex="0"
                    class="theme-button"
                    @click="onButton3Click"
                    @keypress.enter="onButton3Click">
                    <span class="theme-button-text">{{ button3Label }}</span>
                </button>
            </div>
        </template>
    </pop-in>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import PopIn from "@/templates/Popin.vue";
import StopIcon from "@/scalar/stop-icon.svg";

const { t } = useI18n();

const props = withDefaults(defineProps<{
    message?: string;
    icon?: string;
    act1label?: string | null;
    act2label?: string | null;
    act3label?: string | null;
    act1event?: string | null;
    act2event?: string | null;
    act3event?: string | null;
    exitEvent?: string | null;
}>(), {
    message: "",
    icon: "",
    act1label: null,
    act2label: null,
    act3label: null,
    act1event: null,
    act2event: null,
    act3event: null,
    exitEvent: null
});

const emit = defineEmits<{
    close: [];
    [event: string]: [];
}>();

const eventRegex = /^([a-z]+)([a-z-]+)?([a-z]+)?$/;

function getButtonLabel(label: string | null | undefined): string {
    if (!label) { return ""; }
    return t(label).toString();
}

function fireButtonClickEvent(event: string | null | undefined): void {
    if (!event || !eventRegex.test(event)) {
        emit("close");
        return;
    }
    emit(event);
}

function onButton1Click(): void { fireButtonClickEvent(props.act1event); }
function onButton2Click(): void { fireButtonClickEvent(props.act2event); }
function onButton3Click(): void { fireButtonClickEvent(props.act3event); }

function onPopinClose(): void {
    const event = props.exitEvent;
    if (!event || !eventRegex.test(event)) { emit("close"); return; }
    emit(event);
}

const iconName = computed((): string => {
    if (props.icon !== "stop") { return ""; }
    return props.icon;
});

const button1Label = computed((): string => {
    const label = getButtonLabel(props.act1label);
    if (label.length < 1) {
        return t("taskDialog.okButtonLabel").toString();
    }
    return label;
});

const button2Label = computed((): string => getButtonLabel(props.act2label));
const button3Label = computed((): string => getButtonLabel(props.act3label));
const messageText = computed((): string => t(props.message ?? "").toString());
</script>

<style>
.task-dialog {
    border: var(--active-colour) solid 1px;
    box-shadow: .25em .25em .25em black;
}

@media screen and (min-width: 768px) {
    .task-dialog {
        width: 40em;
        left: calc(50vw - 20em);
    }
}
</style>

<style scoped>
.task-dialog {
    border-top: var(--ink-colour) solid 1px;
}

@media screen and (min-width: 768px) {
    .task-dialog {
        width: 40em;
        left: calc(50vw - 20em);
    }
}

.task-message-container {
    display: flex;
    flex-direction: row;
    border-top: var(--ink-colour) solid 1px;
    align-items: center;
}

.task-message {
    cursor: default;
    font-size: larger;
    margin: 1em 0.5em 1em 0.5em;
}

.task-buttons {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    border-top: var(--hover-colour) solid 1px;
    padding: 0.5em;
}

.task-message-icon {
    flex-shrink: 0;
    margin-left: .75em;
    margin-right: 0;
}
</style>
